import {
    MagnifyingGlass,
    PlusButtonIcon,
    UpAndDownArrowsIcon,
} from '@/shared/assets'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu'
import { UTM_PARAM_NAME_ALLOWED_CHARS } from '@synapex/shared2/utils/utm'
import clsx from 'clsx'
import { CSSProperties, useEffect, useState } from 'react'
import SoftChevronIcon from '../../assets/icons/chevronGreySoft.svg'
import { TableLoadingIndicator } from '../loading-indicator/LoadingIndicator'
import { UtmBuilderItem, UtmBuilderItemGenerator } from './UtmBuilderItem'

interface UtmBuilderFieldProps {
    createEntityHandler: () => Promise<void>
    items: UtmBuilderItem[]
    fieldTitle: string
    placeholder: string
    selectedId: string
    inputValue: string
    isLoading: boolean
    setInputValue: (newValue: string) => void
    onSelectedIdChange?: (newValue: string) => void

    rootStyle?: CSSProperties
    headerStyle?: CSSProperties
}

export const UtmBuilderField = ({
    createEntityHandler,
    items,
    fieldTitle,
    placeholder,
    selectedId,
    inputValue,
    isLoading,
    setInputValue,
    onSelectedIdChange,
    rootStyle,
    headerStyle,
}: UtmBuilderFieldProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [isValid, setIsValid] = useState(false)
    const [selectedValue, setSelectedValue] = useState(
        UtmBuilderItemGenerator.empty()
    )
    const [sorting, setSorting] = useState(TableSorting.defaultSorting)
    const [sortedItems, setSortedItems] = useState<UtmBuilderItem[]>([])

    function itemSelected(item: UtmBuilderItem) {
        setSelectedValue(item)
        onSelectedIdChange?.(item.id)
        setIsOpen(false)
        setInputValue('')
    }

    function inputOnChange(value: string) {
        const filtered = value.replace(UTM_PARAM_NAME_ALLOWED_CHARS, '').trim()
        setInputValue(filtered)
    }

    function isInputValid(value: string): boolean {
        return value.trim() != ''
    }

    function createButtonClicked() {
        if (!isInputValid(inputValue)) {
            return
        }
        createEntityHandler()
    }

    function sortButtonClicked() {
        if (sorting == TableSorting.nameAsc) {
            const sorted = [...sortedItems].sort((a, b) =>
                b.name.localeCompare(a.name)
            )
            setSortedItems(sorted)
            setSorting(TableSorting.nameDesc)
        } else {
            const sorted = [...sortedItems].sort((a, b) =>
                a.name.localeCompare(b.name)
            )
            setSortedItems(sorted)
            setSorting(TableSorting.nameAsc)
        }
    }

    function handleClear() {
        const value = UtmBuilderItemGenerator.empty()
        setSelectedValue(value)
        onSelectedIdChange?.(value.id)
    }

    useEffect(() => {
        if (items.length != sortedItems.length) {
            const sorted = items.sort((a, b) => {
                return (
                    new Date(b.created_at).getTime() -
                    new Date(a.created_at).getTime()
                )
            })
            setSortedItems(sorted)
        }
    }, [items, sortedItems])

    useEffect(() => {
        const valid = isInputValid(inputValue)
        setIsValid(valid)
    }, [inputValue])

    useEffect(() => {
        const value = sortedItems.find((x) => x.id == selectedId)
        if (value) {
            setSelectedValue(value)
        } else {
            const value = UtmBuilderItemGenerator.empty()
            setSelectedValue(value)
        }
    }, [selectedId, sortedItems])

    return (
        <div
            style={rootStyle}
            className="table-select-dropdown flex items-center justify-center"
        >
            <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                <DropdownMenuTrigger asChild>
                    <div
                        style={headerStyle}
                        className="table-select-dropdown__header flex h-9 w-full justify-between"
                    >
                        {selectedValue.id != '' ? (
                            <div className="flex items-center justify-center gap-2 truncate">
                                <div
                                    className="h-3 w-3 min-w-3 rounded-full"
                                    style={{
                                        backgroundColor: selectedValue.color,
                                    }}
                                />
                                <p className="truncate pr-4">
                                    {selectedValue.name}
                                </p>
                            </div>
                        ) : (
                            <p className="truncate text-gray-400">
                                {placeholder}
                            </p>
                        )}
                        <img
                            src={SoftChevronIcon}
                            className={clsx(
                                'table-select-dropdown__header__arrow',
                                {
                                    open: isOpen,
                                }
                            )}
                            alt="Toggle Dropdown"
                        />
                    </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    side="bottom"
                    sideOffset={5}
                    align="start"
                    className="border-gray z-50 w-[202px] rounded-xl border-[1px] bg-white px-3 py-[10px]"
                >
                    <div className="flex flex-col gap-2">
                        <p className="text-xs">выбрать метку {fieldTitle}</p>
                        <div className="flex w-fit items-center gap-3">
                            <div className="border-gray flex h-[30px] w-[116px] flex-row items-center gap-2 truncate rounded-lg border-[1px] p-[10px]">
                                <img
                                    className="absolute h-[17px]"
                                    src={MagnifyingGlass}
                                    alt="Search"
                                />
                                <input
                                    type="text"
                                    value={inputValue}
                                    className="w-full py-2 pl-6 pr-0"
                                    onChange={(e) =>
                                        inputOnChange(e.target.value)
                                    }
                                />
                            </div>
                            <button
                                onClick={createButtonClicked}
                                disabled={!isValid}
                                className={clsx(
                                    'rounded-lg',
                                    isValid
                                        ? 'hover:bg-blue-200'
                                        : 'cursor-not-allowed'
                                )}
                            >
                                <img
                                    className="h-5 w-5"
                                    src={PlusButtonIcon}
                                    alt={`Create ${fieldTitle}`}
                                />
                            </button>
                            <button
                                onClick={sortButtonClicked}
                                className={clsx(
                                    'rounded-lg',
                                    'hover:bg-gray-200'
                                )}
                            >
                                <img
                                    className="h-5 w-5"
                                    src={UpAndDownArrowsIcon}
                                    alt={`Sort`}
                                />
                            </button>
                        </div>
                        <EntityTable
                            items={sortedItems}
                            searchText={inputValue}
                            selectedRowId={selectedValue.id}
                            isLoading={isLoading}
                            handleSelect={itemSelected}
                        />
                    </div>
                </DropdownMenuContent>
            </DropdownMenu>
            {selectedValue.id != '' && (
                <button
                    className="absolute right-[30px] select-none justify-center text-[18px] text-[#999] transition-colors duration-300 ease-in-out hover:text-[#333]"
                    onClick={handleClear}
                    aria-label="Close modal"
                >
                    ×
                </button>
            )}
        </div>
    )
}

interface EntityTableProps {
    items: UtmBuilderItem[]
    searchText: string
    selectedRowId: string
    isLoading: boolean
    handleSelect: (item: UtmBuilderItem) => void
}

const EntityTable = ({
    items,
    searchText,
    selectedRowId,
    isLoading,
    handleSelect,
}: EntityTableProps) => {
    function isInputValid(value: string): boolean {
        return value == '' || value.trim() != ''
    }

    return (
        <>
            <ul className="scrollbar-thin flex max-h-40 flex-col gap-1 overflow-y-scroll">
                {items
                    .filter(
                        (item) =>
                            isInputValid(searchText) &&
                            item.name
                                .toLowerCase()
                                .startsWith(searchText.toLowerCase())
                    )
                    .map((item) => (
                        <li
                            key={item.id}
                            className={clsx(
                                'flex h-[30px] w-[116px] cursor-pointer flex-row items-center rounded-md',
                                'w-full gap-2 py-[14px] pl-[14px] pr-1',
                                item.id != '' && item.id == selectedRowId
                                    ? 'border-1 border-gray bg-[#e5e5ff]'
                                    : '',
                                'hover:bg-[var(--blue-light)]'
                            )}
                            data-value={item.id}
                            onClick={() => handleSelect(item)}
                        >
                            <div
                                className="h-3 min-w-3 rounded-full"
                                style={{ backgroundColor: item.color }}
                            />
                            <span className="truncate text-[13px]">
                                {item.name}
                            </span>
                        </li>
                    ))}
            </ul>
            {isLoading && <TableLoadingIndicator />}
        </>
    )
}

enum TableSorting {
    defaultSorting,
    nameAsc,
    nameDesc,
}
